import { isOnboardingCountryIso3, ONBOARDING_COUNTRY_ISO3 } from "@/shared/data/onboarding-countries";
import { countries } from "@/shared/utils/countries";

import type { ClientsSelectOption } from "../types/clients.types";

export const CLIENTS_API_ENDPOINTS = {
  /** Flat list — GET /internal/clients */
  LIST: "/internal/clients",
  /** Top-level organization create (admin) — POST /internal/organizations */
  ORGANIZATIONS: "/internal/organizations",
  /** Organization detail/delete (admin) — DELETE /internal/organizations/:id */
  ORGANIZATION_DETAIL_BY_ID: (organizationId: string) => `/internal/organizations/${organizationId}`,
  /** Tree view (admin) — GET /internal/clients/tree */
  TREE: "/internal/clients/tree",
  /** Client detail — GET /internal/clients/:id */
  BY_ID: (id: string) => `/internal/clients/${id}`,
  /** Client accounts — GET|POST /internal/clients/:clientId/accounts */
  ACCOUNTS: (clientId: string) => `/internal/clients/${clientId}/accounts`,
  /** Client account by id — DELETE /internal/clients/:clientId/accounts/:id */
  ACCOUNT_BY_ID: (clientId: string, accountId: string) =>
    `/internal/clients/${clientId}/accounts/${accountId}`,
  ORGANIZATIONS_SEARCH: "/internal/clients/organizations/search",
  /** Organization detail (admin tree) */
  ORGANIZATION_BY_ID: (organizationId: string) => `/internal/clients/organizations/${organizationId}`,
  /**
   * Organization rendered as a client-detail object (real org KYC status/documents) — GET
   * /internal/clients/organizations/:id/client-profile
   */
  ORGANIZATION_CLIENT_PROFILE: (organizationId: string) =>
    `/internal/clients/organizations/${organizationId}/client-profile`,
  /** Organization accounts (admin tree) */
  ORGANIZATION_ACCOUNTS: (organizationId: string) => `/internal/organizations/${organizationId}/accounts`,
  /** Organization account by id — DELETE /internal/organizations/:organizationId/accounts/:id */
  ORGANIZATION_ACCOUNT_BY_ID: (organizationId: string, accountId: string) =>
    `/internal/organizations/${organizationId}/accounts/${accountId}`,
  /** Internal mail — POST /internal/mail/send */
  MAIL_SEND: "/internal/mail/send",
} as const;

export const CLIENTS_PAGE_SIZE = 25;

export const PARENT_CLIENT_LIST_LIMIT = 100;

export const PAGINATION_SIZES = [10, 25, 50, 100] as const;

export const COUNTRY_OPTIONS: ClientsSelectOption[] = [
  ...countries.map((c) => ({
    label: c.name,
    value: c.code,
    iso2: c.iso2,
  })),
];

const ISO2_TO_COUNTRY_OPTION = new Map<string, ClientsSelectOption>(
  COUNTRY_OPTIONS.filter((o): o is ClientsSelectOption & { iso2: string } => Boolean(o.iso2)).map((o) => [
    o.iso2.toUpperCase(),
    o,
  ]),
);

/** Resuelve la opción del selector de país (ISO3 para API) a partir del ISO2 del indicativo telefónico. */
export function getCountrySelectOptionByIso2(iso2: string): ClientsSelectOption | undefined {
  return ISO2_TO_COUNTRY_OPTION.get(iso2.trim().toUpperCase());
}

/**
 * ISO 3166-1 alpha-3 codes offered when creating or filtering a client: the onboarding allowlist, the same 17
 * the public register offers.
 *
 * This was the six countries with a specific document matrix, justified as "the codes the backend accepts via
 * `@IsCountryCode`; selecting any other on organization create returns 400". **That was false** — the enum
 * behind that decorator has 189 entries, verified against `CreateOrganizationDto` in coloca-nest. The cap was
 * a frontend decision, and it made the create form narrower than the register that feeds it.
 */
export const SUPPORTED_COUNTRY_CODES = ONBOARDING_COUNTRY_ISO3;

export function isSupportedCountryCode(code: string | null | undefined): boolean {
  return isOnboardingCountryIso3(code);
}

/** Country options limited to the codes onboarding offers (organization create / identity / filters). */
export const SUPPORTED_COUNTRY_OPTIONS: ClientsSelectOption[] = COUNTRY_OPTIONS.filter((option) =>
  isOnboardingCountryIso3(option.value),
);

/**
 * @deprecated Country-independent list with the wrong passport value (`PASAPORTE`
 * vs backend `PASSPORT`). For new code use `documentTypesFor(country, category)`
 * from `clientDocumentTypes.constants.ts`. Kept only for the legacy `EditClientForm`
 * (Edit flow is out of scope — see plan §9); remove when Edit is migrated.
 */
export const IDENTIFICATION_TYPE_OPTIONS: ClientsSelectOption[] = [
  { label: "Nit", value: "NIT" },
  { label: "Cc", value: "CC" },
  { label: "Ce", value: "CE" },
  { label: "Pasaporte", value: "PASAPORTE" },
  { label: "Ppt", value: "PPT" },
];
